import { useNavigate } from 'react-router-dom'
import { Skeleton } from './Skeleton'
import { InformationCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { ProBadge } from './UpgradePrompt'
import { trackViewPricing } from '../utils/facebookPixel'
import { trackGAViewPricing } from '../utils/googleAnalytics'
import type { ComponentType } from 'react'

type Props = {
  label: string
  value: string
  hint?: string
  valueTooltip?: string
  icon?: ComponentType<{ className?: string }>
  isLoading?: boolean
  proOnly?: boolean
  isPro?: boolean
  compact?: boolean
}

export const StatCard = ({ label, value, hint, valueTooltip, icon: Icon, isLoading = false, proOnly = false, isPro = true, compact = false }: Props) => {
  const navigate = useNavigate()
  const isLocked = proOnly && !isPro

  // Pill variant for compact mode
  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-app-subtle/60 border border-app-line/50">
        {Icon && <Icon className="h-3.5 w-3.5 text-app-muted shrink-0" />}
        <span className="text-xs text-app-muted truncate">{label}</span>
        {hint && (
          <span className="relative inline-flex items-center group">
            <InformationCircleIcon
              className="h-3.5 w-3.5 text-app-muted hover:text-app-muted"
              aria-hidden="true"
            />
            <span className="sr-only">{hint}</span>
            <span
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-[260px] -translate-x-1/2 rounded-lg border border-app-line bg-app-surface px-3 py-2 text-xs text-app-ink opacity-0 shadow-xl transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-none"
            >
              {hint}
            </span>
          </span>
        )}
        {isLoading ? (
          <Skeleton className="h-4 w-8 ml-auto" />
        ) : isLocked ? (
          <div className="ml-auto">
            <ProBadge
              onClick={() => navigate('/pricing')}
              source="stat_card_compact"
              feature={label.toLowerCase().replace(/\s+/g, '_')}
            />
          </div>
        ) : (
          <span
            className="ml-auto text-[15px] font-semibold text-app-ink"
            title={valueTooltip}
          >
            {value}
          </span>
        )}
      </div>
    )
  }

  // Regular card variant
  return (
    <div className={`card p-3 space-y-1.5 ${isLocked ? 'relative' : ''}`}>
      <div className="flex items-center gap-1.5 text-xs text-app-muted">
        {Icon && <Icon className="h-4 w-4 text-app-muted" />}
        <span>{label}</span>
        {proOnly && !isPro && <ProBadge onClick={() => navigate('/pricing')} source="stat_card_badge" feature={label.toLowerCase().replace(/\s+/g, '_')} />}
        {hint ? (
          <span className="relative inline-flex items-center group">
            <InformationCircleIcon
              className="h-4 w-4 text-app-muted hover:text-app-muted"
              aria-hidden="true"
            />
            <span className="sr-only">{hint}</span>
            <span
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-max max-w-[260px] -translate-x-1/2 rounded-lg border border-app-line bg-app-surface px-3 py-2 text-xs text-app-ink opacity-0 shadow-xl transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-none"
            >
              {hint}
            </span>
          </span>
        ) : null}
      </div>
      {isLoading ? (
        <div className="space-y-1.5">
          <Skeleton className="h-7 w-24" />
        </div>
      ) : isLocked ? (
        <button
          onClick={() => {
            trackViewPricing('stat_card', label)
            trackGAViewPricing('stat_card', label)
            navigate('/pricing')
          }}
          data-event-category="navigation"
          data-event-action="view_pricing"
          data-event-label={`stat_locked_${label.toLowerCase().replace(/\s+/g, '_')}`}
          data-destination="/pricing"
          data-source="stat_card"
          data-stat-name={label}
          className="flex items-center gap-2 text-app-muted hover:text-app-muted transition-colors"
        >
          <LockClosedIcon className="h-5 w-5" />
          <span className="text-sm">Upgrade to view</span>
        </button>
      ) : (
        <p className="text-xl font-semibold text-app-ink">{value}</p>
      )}
    </div>
  )
}

